import { ComponentPropsWithoutRef, forwardRef } from "react";
import cx from "classnames";
import { LayoutUtilProps } from "../../types";
import styles from "./Card.module.scss";
import { Size } from "../../types";
import { useLayoutPropsUtil } from "../../hooks";

/**
 * Props for the Card component
 * @extends ComponentPropsWithoutRef<"div">
 * @extends LayoutUtilProps
 */
export type CardProps = ComponentPropsWithoutRef<"div"> &
  LayoutUtilProps & {
    /**
     * The background variant of the card.
     * @default undefined
     */
    background?: "strong" | "stronger";
    /**
     * The padding size of the card.
     * @default "medium"
     */
    padding?: Extract<Size, "0" | "xsmall" | "small" | "medium" | "large">;
  };

/**
 * Card component for displaying content in a contained, elevated container.
 *
 * Features:
 * - Configurable padding sizes (0, xsmall, small, medium, large)
 * - Background variants (strong, stronger) for visual emphasis
 * - Responsive design with automatic layout utilities
 * - Consistent border radius and styling
 * - Support for all standard div props
 * - Layout utility props for positioning and spacing
 *
 * @example
 * <Card padding="large" background="strong">
 *   <h2>Card Title</h2>
 *   <p>Card content goes here</p>
 * </Card>
 */
export const Card = forwardRef<HTMLDivElement, CardProps>((props, ref) => {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    children,
    className,
    padding = "medium",
    background,
    style,
    ...rest
  } = componentProps;

  const cardClassNames = cx(
    [styles["card"]],
    [styles[`padding-${padding}`]],
    className,
    {
      [styles[`bg-${background}`]]: !!background,
    },
  );

  const styleCombined = {
    ...style,
    ...layoutStyles,
  };

  return (
    <div
      className={cardClassNames}
      data-anv="card"
      style={styleCombined}
      ref={ref}
      {...rest}
    >
      {children}
    </div>
  );
});

Card.displayName = "Card";
